import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { LogoSGOSTU } from 'common/Logos/Logo-SGOSTU';
import './Hero.css';
import { useLanguage } from 'hooks/useLanguage';

gsap.registerPlugin(ScrollTrigger);

/** Реєстр e-phan — обидві дії Hero ведуть у той самий кабінет: реєстрація і вхід
 * до бази даних — це різні екрани одного застосунку, не різні сервіси. */
const EPHAN_URL = 'https://app.e-phan.com';

const TITLE_LINES = {
  en: ['Dancesport public organisations', 'union of Ukraine'],
  ua: ['Спілка громадських організацій', 'спортивного танцю України'],
};

export default function Hero() {
  const language = useLanguage();

  const heroSectionRef = useRef(null);
  const heroInnerRef = useRef(null);
  const heroVisualRef = useRef(null);

  // Паралакс виходу з Hero — ADR-0011. Вміст піднімається й тане швидше за
  // природний скрол, підказуючи перехід до наступної секції. Знак і
  // абстракції праворуч ідуть за тим самим скролом, але повільніше за текст
  // (yPercent -32 проти -18): різниця темпу — і є відчуття глибини, а не двох
  // шарів, що просто їдуть разом.
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroSectionRef.current || !heroInnerRef.current) return undefined;

    const ctx = gsap.context(() => {
      const scrollTrigger = {
        trigger: heroSectionRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      };

      gsap.to(heroInnerRef.current, {
        yPercent: -18,
        opacity: 0.35,
        ease: 'none',
        scrollTrigger,
      });

      if (heroVisualRef.current) {
        gsap.to(heroVisualRef.current, {
          yPercent: -32,
          opacity: 0.4,
          ease: 'none',
          scrollTrigger,
        });
      }
    }, heroSectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className='hero grid-rule brand-watermark' ref={heroSectionRef}>
      <span className='hero-aurora' aria-hidden='true' />

      <div className='container hero-layout'>
        <div className='hero-inner' ref={heroInnerRef}>
          <h1 className='hero-title'>
            {TITLE_LINES[language].map((line, index) => (
              <span className='hero-line' key={line}>
                <span className='hero-line-inner' style={{ '--i': index }}>
                  {line}
                </span>
              </span>
            ))}
          </h1>

          <p className='hero-lede'>
            {language === 'en'
              ? 'We are building a new digital future for sports dance — a unified register of participants, digital certification, a transparent career-growth system, and new opportunities for development. Join our community today!'
              : 'Ми формуємо нове цифрове майбутнє спортивних танців — єдиний реєстр учасників, цифрова сертифікація, прозора система кар’єрного зростання та нові можливості для розвитку. Приєднуйся до нашої спільноти вже зараз!'}
          </p>

          <div className='hero-actions'>
            <span className='hero-cta'>
              <a className='btn btn--primary' href={EPHAN_URL} target='_blank' rel='noreferrer'>
                {language === 'en' ? 'Register' : 'Зареєструватися'}
              </a>
            </span>
            <span className='hero-cta'>
              <a className='btn btn--secondary' href={EPHAN_URL} target='_blank' rel='noreferrer'>
                {language === 'en' ? 'Database login' : 'Вхід до бази даних'}
              </a>
            </span>
          </div>
        </div>

        {/* Фото тут не працювало — стокова студійна пара поруч зі справжнім
            текстом організації читалася як банер, чужий сторінці. Замість
            нього — знак СГОСТУ, укрупнений і розібраний на власні два шари
            кольору (синій, потім жовтий), та абстрактні форми навколо: та ж
            мова, що в .hero-aurora, тільки тут вона головна, не фонова.

            Два вкладені шари — щоб transform скрол-паралаксу (.hero-visual,
            GSAP) і transform входу (.hero-visual-inner, CSS) не переписували
            один одного на тому самому вузлі. */}
        <div className='hero-visual' ref={heroVisualRef} aria-hidden='true'>
          <div className='hero-visual-inner'>
            <span className='hero-visual-blob hero-visual-blob--brand' />
            <span className='hero-visual-blob hero-visual-blob--accent' />
            <span className='hero-visual-ring hero-visual-ring--dashed' />
            <span className='hero-visual-ring hero-visual-ring--solid' />
            <span className='hero-visual-dots' />
            <span className='hero-visual-mark'>
              <LogoSGOSTU classname='hero-visual-mark-svg' />
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
